import React, { useEffect, useState } from 'react';
import type { KeyboardEventHandler, LegacyRef } from 'react';
import {
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    Grid,
    GridItem,
    RadioGroup,
    useBreakpointValue,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { ShippingMethodId } from '../../bff-api/baskets/model/ShippingMethodId';
import { useStoreSearch } from '../../bff-api/stores/useStoreSearch';
import { useBopisStore, useBopisStoreMutation } from '../../hooks/useBopisStore';
import { Button } from '../../ui-components';
import { Drawer } from '../../ui-components/drawer';
import { Field } from '../field';
import { ZIP_CODE_FIELD, useZipCodeField } from '../forms/useZipCodeField';
import { BopisStoreSearchItem } from './store-item';
import type { Store, Stores } from '../../bff-api/stores/model';

interface Props {
    id: string;
    productId?: string;
    isOpen: boolean;
    isLoading: boolean;
    onClose: () => void;
    onChangeStore?: (store: Store) => Promise<void> | void;
    closeOnPickUpStore?: boolean;
}

export const BopisStoreSearchDrawer: React.FC<Props> = ({
    id,
    productId,
    isOpen,
    isLoading,
    onClose,
    onChangeStore,
    closeOnPickUpStore = false,
}: Props) => {
    const { data: store } = useBopisStore();
    const zipCode = store?.postalCode ?? '';
    const [selectedStoreId, setSelectedStoreId] = useState(store?.id ?? '');
    const [isLoadingStores, setLoadingStores] = useState(false);
    const [stores, setStores] = useState<Stores>([]);
    const intl = useIntl();
    const form = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: { zipCode },
    });
    const storeSearchInventory = useStoreSearch();
    const bopisStoreMutation = useBopisStoreMutation();

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = async (e): Promise<void> => {
        if (!isSearchDisabled && e.key === 'Enter') {
            await onClickSearch();
        }
    };

    const { helpText, label, labelRef, ...zipCodeField } = useZipCodeField(form, onKeyDown);

    const isFullPageDrawer = useBreakpointValue({
        base: true,
        md: false,
    });

    useEffect(() => {
        form.setValue(ZIP_CODE_FIELD, zipCode);
    }, [form, zipCode]);

    useEffect(() => {
        if (!isLoading) {
            onClose();
        }
    }, [isLoading, onClose]);

    const postalCode = form.watch(ZIP_CODE_FIELD);

    const onClickSearch = async (): Promise<void> => {
        setLoadingStores(true);

        const storesFound = await storeSearchInventory.mutateAsync({
            postalCode,
            productId,
            shippingMethodId: ShippingMethodId.BOPIS,
        });
        setStores(storesFound);

        if (!storesFound.length) {
            form.setError(ZIP_CODE_FIELD, {
                type: 'custom',
                message: intl.formatMessage({
                    id: 'store.search.no.stores.found',
                    defaultMessage: 'No result found for your search',
                }),
            });
        }

        let newSelectedStoreId = storesFound.length ? storesFound[0].id : '';
        if (store) {
            const currentStore = storesFound.find((storeFound) => storeFound.id === store.id);

            if (currentStore && currentStore.c_bopisAvailability) {
                newSelectedStoreId = currentStore.id;
            }
        }

        if (selectedStoreId !== newSelectedStoreId) {
            setSelectedStoreId(newSelectedStoreId);
        }

        setLoadingStores(false);
    };

    const onPickStore = async (): Promise<void> => {
        if (store?.id !== selectedStoreId) {
            const newStore = stores.find((item) => item.id === selectedStoreId)!;
            bopisStoreMutation.mutate(newStore);

            await onChangeStore?.(newStore);

            if (closeOnPickUpStore) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const isSelectedStoredAvailable =
        (stores.find((item) => item.id === selectedStoreId)?.c_bopisAvailability ?? 0) > 0;

    const isSearchDisabled = !postalCode || !!form.formState.errors?.[ZIP_CODE_FIELD];

    return (
        <Drawer
            id={id}
            title={intl.formatMessage({
                id: 'store.search.title',
                defaultMessage: 'Select a Store',
            })}
            isOpen={isOpen}
            onClose={onClose}
            callToAction={{
                children: intl.formatMessage({
                    id: 'store.search.pick.up.at.this.store',
                    defaultMessage: 'Pick up at this store',
                }),
                onClick: onPickStore,
                isDisabled: !!(
                    isLoadingStores ||
                    !stores.length ||
                    (productId && (!selectedStoreId || !isSelectedStoredAvailable))
                ),
                isLoading: bopisStoreMutation.isLoading || isLoading,
            }}
            isFullPage={isFullPageDrawer}
        >
            <FormControl variant={zipCodeField.variant}>
                <Grid
                    templateAreas={`"label label"
                                  "field cta"
                                  "helper-text helper-text"`}
                    gridTemplateColumns="auto max-content"
                    columnGap={2}
                >
                    <GridItem area="label">
                        <FormLabel
                            ref={labelRef as LegacyRef<HTMLLabelElement>}
                            htmlFor={zipCodeField.id}
                            data-testid={`${zipCodeField.id}-label`}
                            variant={zipCodeField.variant}
                        >
                            {label}
                        </FormLabel>
                    </GridItem>
                    <GridItem area="field">
                        <Field {...zipCodeField} />
                    </GridItem>
                    <GridItem area="helper-text">
                        <FormHelperText data-testid={`${zipCodeField.id}-helper-message`}>
                            {helpText}
                        </FormHelperText>
                    </GridItem>
                    <GridItem area="cta">
                        <Button
                            id={`${id}-search-button`}
                            variant="secondary"
                            isLoading={isLoadingStores}
                            isDisabled={isSearchDisabled}
                            onClick={onClickSearch}
                        >
                            <FormattedMessage id="store.search" defaultMessage="Search" />
                        </Button>
                    </GridItem>
                </Grid>
            </FormControl>

            <Flex direction="column" marginTop={6}>
                <RadioGroup onChange={setSelectedStoreId} value={selectedStoreId}>
                    {stores.map((item, idx) => (
                        <React.Fragment key={item.id}>
                            <BopisStoreSearchItem store={item} ignoreInventory={!productId} />

                            {stores.length !== idx + 1 ? (
                                <div className="my-6 w-full border-t border-t-slt-pepper-400" />
                            ) : null}
                        </React.Fragment>
                    ))}
                </RadioGroup>
            </Flex>
        </Drawer>
    );
};

BopisStoreSearchDrawer.defaultProps = {
    productId: undefined,
    onChangeStore: undefined,
    closeOnPickUpStore: false,
};
